/**
 * Casino Service for user-modern
 * Fetches enabled providers/games from tenant-management gaming-controls API
 * Supports both AMB (AskMeBet) and RB7 gaming platforms
 */

const getBaseURL = () =>
  (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8081'

function getAccessToken(): string | null {
  return localStorage.getItem('accessToken')
}

function getCurrentUser(): { id?: string; username?: string; tenantId?: string } | null {
  try {
    const s = localStorage.getItem('user')
    return s ? JSON.parse(s) : null
  } catch {
    return null
  }
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Provider {
  providerId: string
  name: string
  /** AMB category: EGAMES | LIVECASINO | SPORT | POKER | KENO | '' (RB7 = empty/null) */
  category: string
  gameCount: number
  logoUrl?: string
  sortOrder?: number
  isEnabled: boolean
  ambDisabled?: boolean
  /** Inferred: providers with empty category are assumed to be RB7 */
  platform?: 'amb' | 'rb7'
}

export interface Game {
  gameCode: string
  name: string
  imageUrl: string
  rank?: number | null
  isEnabled: boolean
  ambDisabled?: boolean
}

export interface LaunchResult {
  gameUrl: string
}

// ─── Game Session Management (ป้องกันถอนขณะเล่นเกม) ────────────────────────

export const CASINO_SESSION_KEY = 'casino_game_session'

export interface GameSession {
  active: boolean
  gameCode: string
  gameName: string
  providerName: string
  startTime: number
}

export function setGameSession(session: GameSession): void {
  localStorage.setItem(CASINO_SESSION_KEY, JSON.stringify(session))
}

export function clearGameSession(): void {
  localStorage.removeItem(CASINO_SESSION_KEY)
}

export function getGameSession(): GameSession | null {
  try {
    const s = localStorage.getItem(CASINO_SESSION_KEY)
    return s ? (JSON.parse(s) as GameSession) : null
  } catch {
    return null
  }
}

export function isInGameSession(): boolean {
  return getGameSession()?.active === true
}

const AMB_LOGIN_RETRY_DELAYS_MS = [400, 1200]

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function isRetryableAmbLaunchError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)

  return (
    message.includes('HTTP 502') ||
    message.includes('HTTP 503') ||
    message.includes('HTTP 504') ||
    message.includes('Failed to route API request') ||
    message.toLowerCase().includes('upstream error')
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

async function casinoFetch<T>(path: string, tenantId: string, options?: RequestInit): Promise<T> {
  const token = getAccessToken()
  const url = `${getBaseURL()}${path}`
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Tenant-ID': tenantId,
      ...(options?.headers as Record<string, string>),
    },
  })
  if (!res.ok) {
    const errText = await res.text().catch(() => '')
    throw new Error(`HTTP ${res.status}: ${errText}`)
  }
  return res.json() as Promise<T>
}

/** Detect platform from provider — RB7 providers typically have no AMB category */
function inferPlatform(provider: Provider): 'amb' | 'rb7' {
  const ambCategories = ['EGAMES', 'LIVECASINO', 'SPORT', 'POKER', 'KENO', 'FISHING']
  return ambCategories.includes(provider.category?.toUpperCase()) ? 'amb' : 'rb7'
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Get all enabled providers for this tenant
 * Requires: user JWT in localStorage as 'accessToken'
 */
export async function getProviders(tenantId: string): Promise<Provider[]> {
  const data = await casinoFetch<{ success: boolean; data: Provider[] }>(
    '/api/v1/tenant-management/gaming-controls/providers?env=production',
    tenantId,
  )
  if (!data.success) return []
  return (data.data || [])
    .filter((p) => p.isEnabled)
    .map((p) => ({ ...p, platform: inferPlatform(p) }))
}

/**
 * Get all enabled games for a specific provider
 * Results are cached by caller via gamesCache state
 */
export async function getProviderGames(tenantId: string, providerId: string): Promise<Game[]> {
  const data = await casinoFetch<{ success: boolean; data: Game[] }>(
    `/api/v1/tenant-management/gaming-controls/providers/${encodeURIComponent(providerId)}/games?env=production`,
    tenantId,
  )
  if (!data.success) return []
  return (data.data || []).filter((g) => g.isEnabled)
}

/**
 * Launch an AMB game — returns a URL to embed in an iframe
 * callbackUrl: where the game redirects when player exits (our lobby URL)
 * Requires: user JWT (AMB service reads username from token)
 */
export async function launchAmbGame(
  tenantId: string,
  productId: string,
  gameCode: string,
  isMobileLogin = false,
  callbackUrl?: string,
): Promise<LaunchResult> {
  const payload = JSON.stringify({
    productId,
    gameCode,
    isMobileLogin,
    currency: 'THB',
    language: 'th',
    ...(callbackUrl ? { callbackUrl } : {}),
  })

  let lastError: unknown = null

  for (let attempt = 0; attempt <= AMB_LOGIN_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const data = await casinoFetch<{
        success: boolean
        data?: { gameUrl?: string }
        gameUrl?: string
        status?: number
        message?: string
      }>('/api/v1/amb/seamless/login', tenantId, {
        method: 'POST',
        body: payload,
      })

      const gameUrl = data.data?.gameUrl || (data as Record<string, unknown>).gameUrl
      if (gameUrl) {
        return { gameUrl: String(gameUrl) }
      }

      const upstreamMessage = data.message || 'Upstream error'
      if (data.status === 900 || upstreamMessage.toLowerCase().includes('upstream error')) {
        throw new Error(`AMB upstream error: ${upstreamMessage}`)
      }

      throw new Error('ไม่ได้รับ URL สำหรับเปิดเกม')
    } catch (error) {
      lastError = error

      if (attempt >= AMB_LOGIN_RETRY_DELAYS_MS.length || !isRetryableAmbLaunchError(error)) {
        throw error
      }

      await sleep(AMB_LOGIN_RETRY_DELAYS_MS[attempt])
    }
  }

  throw (lastError instanceof Error ? lastError : new Error('เปิดเกมไม่สำเร็จ'))
}

/**
 * Launch an RB7 game — returns a URL to embed in an iframe
 * No auth required by RB7 service, userId passed as query param
 */
export async function launchRb7Game(
  provider: string,
  gameCode: string,
  isMobile = false,
  tenantId?: string,
): Promise<LaunchResult> {
  const user = getCurrentUser()
  const userId = user?.id || '1'
  const params = new URLSearchParams({
    game: provider,
    gameCode,
    userId,
    isMobile: String(isMobile),
  })
  if (tenantId) {
    params.set('tenantId', tenantId)
  }
  const res = await fetch(`${getBaseURL()}/api/v1/rb7/launch?${params.toString()}`, {
    headers: tenantId ? { 'X-Tenant-ID': tenantId } : undefined,
  })
  if (res.status === 403) {
    let message = 'บัญชีนี้ถูกปิดสิทธิ์การเข้าใช้งานเกมส์ กรุณาติดต่อแอดมิน'
    try {
      const body = await res.json()
      if (body?.message) message = body.message
    } catch { /* keep default message */ }
    throw new Error(message)
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const data = await res.json()
  const gameUrl = data.url || data.gameUrl || data.launchUrl || data.data?.url
  if (!gameUrl) throw new Error('ไม่ได้รับ URL สำหรับเปิดเกม')
  return { gameUrl }
}

/**
 * Launch a game — auto-detects AMB vs RB7 from provider platform.
 * callbackUrl is passed to AMB to redirect back to lobby when player exits game.
 */
export async function launchGame(
  tenantId: string,
  provider: Provider,
  game: Game,
  isMobile = false,
  callbackUrl?: string,
): Promise<LaunchResult> {
  if (provider.platform === 'rb7') {
    return launchRb7Game(provider.providerId, game.gameCode, isMobile, tenantId)
  }
  return launchAmbGame(tenantId, provider.providerId, game.gameCode, isMobile, callbackUrl)
}

/** Check if user is authenticated */
export function isLoggedIn(): boolean {
  return !!getAccessToken()
}

// ─── Game Stats (Popular / Trending) ────────────────────────────────────────

export interface PopularGame {
  rank:         number
  gameCode:     string
  providerId:   string
  gameName:     string
  providerName: string
  category:     string
  imageUrl:     string
  playCount:    number
  weeklyCount:  number
  count:        number
  percentage:   number
  lastPlayedAt: string
}

/**
 * Fire-and-forget: track that a game was launched.
 * Never throws — errors are silently ignored so they never block gameplay.
 */
export async function trackGamePlay(
  tenantId: string,
  game: Game,
  provider: Provider,
): Promise<void> {
  try {
    await casinoFetch('/api/v1/tenant-management/game-stats/track', tenantId, {
      method: 'POST',
      body: JSON.stringify({
        gameCode:     game.gameCode,
        providerId:   provider.providerId,
        gameName:     game.name,
        providerName: provider.name,
        category:     provider.category || '',
        imageUrl:     game.imageUrl || '',
      }),
    })
  } catch {
    // Intentionally swallowed — tracking failure must never affect gameplay
  }
}

/**
 * Fetch the popular games list sorted by play count.
 * mode: 'alltime' (default) | 'weekly'
 */
export async function getPopularGames(
  tenantId: string,
  limit = 30,
  mode: 'alltime' | 'weekly' = 'alltime',
): Promise<{ data: PopularGame[]; total: number }> {
  try {
    const res = await casinoFetch<{ success: boolean; data: PopularGame[]; total: number }>(
      `/api/v1/tenant-management/game-stats/popular?limit=${limit}&mode=${mode}`,
      tenantId,
    )
    return { data: res.data || [], total: res.total || 0 }
  } catch {
    return { data: [], total: 0 }
  }
}
